import React, { useRef, useState, useEffect } from "react";
import { styled } from "@styles";
import Select from "react-select";

import { IProps } from "./types";
import { Col, Container, Row } from "react-bootstrap";
import { product_product_variants } from "@components/ProductPage/ProductDetailPage/__generated__/product";
import Check from "@images/icons/product-detail-variant-check.png";
import OutOfStock from "@images/icons/product-detail-out-of-stock.png";
import { variant } from "@src/pages/Cart/__generated__/variant";
import { useContainerDimensions } from "@src/core/utils";

const Wrapper = styled.div<{ minimizePadding: boolean }>`
  background: #f5f5f5;
  padding: ${(props) => (props.minimizePadding ? "0" : "1rem 0.5rem")};
  margin: ${(props) => (props.minimizePadding ? "0" : "0.5rem 0")};
`;

const VariantImgFrame = styled.div<{
  selected: boolean;
  isOutOfStock: boolean;
  enableInteraction: boolean;
}>`
  background: ${(props) =>
    props.isOutOfStock ? "rgba(0, 0, 0, 0.48)" : "#ffffff"};
  //background: #3dcc4a; // for test
  display: inline-block;
  box-shadow: 1px 1px 2px 0 rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  border-style: solid;
  border-color: ${(props) => (props.selected ? "#000000" : "#ffffff")};
  margin: 0.4rem;
  padding: 0.3rem;

  :hover {
    cursor: ${(props) => props.enableInteraction ? "pointer" : ""};
  }

  position: relative;
  text-align: center;
`;

const VariantImg = styled.img<{ size: number }>`
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  display: block;
`;

const VariantSelectedCheckImg = styled.img<{ size: number }>`
  position: absolute;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  top: 0;
  left: 0;
  margin: 4.8px;
`;

const VariantOutOfStockImg = styled.img<{ size: number }>`
  position: absolute;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  top: 0;
  left: 0;
  margin: 4.8px;
`;

const VariantSelectionBoxWrapper = styled.div`
  width: 100%;
  display: block;
  margin-top: 1.1rem;
  font-size: 0.9rem;
`;

const selectStyles = {
  // failed to change text color
  // https://react-select.com/styles
  singleValue: (styles) => {
    return {
      ...styles,
      color: "#3dcc4a",
    };
  },
};

interface ProductVariantSelectBoxIProps {
  sortedVariants: (product_product_variants | variant | null)[];
  selectedVariant: number | null;
  handleChange: any;
  disableOutOfStock?: boolean | null;
}

export const ProductVariantSelectBox: React.FC<ProductVariantSelectBoxIProps> = ({
  sortedVariants,
  selectedVariant = null,
  handleChange,
  disableOutOfStock = true,
}) => {
  const options = sortedVariants.map((v, i) => {
    return {
      value: i,
      label: `${v.attributes[0].value.name}${v.stockQuantity <= 0 ? ' (Out of Stock)' : ''}`,
      isOutOfStock: v.stockQuantity <= 0,
    };
  });

  return (
    <VariantSelectionBoxWrapper>
      <Select
        defaultValue={selectedVariant}
        value={options[selectedVariant]}
        onChange={(selected) => handleChange(selected.value)}
        options={options}
        style={selectStyles}
        placeholder="-- Select Variant --"
      />
    </VariantSelectionBoxWrapper>
  );
};

const ProductDetailVariantSelection: React.FC<IProps> = ({
  sortedVariants,
  selectedVariant = null,
  setSelectedVariant = null,
  enableInteraction = true,
  minimizePadding = false,
}: IProps) => {
  const componentRef = useRef();
  const { width, height } = useContainerDimensions(componentRef);

  const itemsPerLine = 6;
  const itemWidth = 12 / itemsPerLine;
  const totalVariants = sortedVariants.length ?? 0;
  const numOfVariantsRows = Math.floor(totalVariants / itemsPerLine) + 1;

  const imageSize = (width - 150) / itemsPerLine;

  let rows = Array.from(Array(numOfVariantsRows), () =>
    Array<product_product_variants | variant>(itemsPerLine)
  );

  sortedVariants.forEach((v, i) => {
    const row = Math.floor(i / itemsPerLine);
    const col = i % itemsPerLine;
    rows[row][col] = v;
  });

  const handleChange = (index) => {
    if (setSelectedVariant !== null) {
      setSelectedVariant(index);
    }
  };

  return (
    <Wrapper minimizePadding={minimizePadding}>
      <Container ref={componentRef}>
        {rows.map((r, i) => {
          return (
            <Row key={i}>
              {r.map((c, j) => {
                const image = c?.attributes[0]?.value?.image;
                const src = image?.url ?? "";
                const alt = image?.alt ?? "";
                const index = i * itemsPerLine + j;
                const selected = index === selectedVariant;
                const isOutOfStock = c.stockQuantity <= 0;
                const props = { selected, isOutOfStock, enableInteraction };
                return (
                  <Col key={j} xs={itemWidth}>
                    <VariantImgFrame {...props}>
                      <VariantImg
                        size={imageSize}
                        src={src}
                        alt={alt}
                        onClick={() => handleChange(index)}
                      />
                      {selected && (
                        <VariantSelectedCheckImg src={Check} size={imageSize} />
                      )}
                      {isOutOfStock && (
                        <VariantOutOfStockImg
                          size={imageSize}
                          src={OutOfStock}
                          onClick={() => handleChange(index)}
                        />
                      )}
                    </VariantImgFrame>
                  </Col>
                );
              })}
            </Row>
          );
        })}
        {enableInteraction && (
          <ProductVariantSelectBox
            sortedVariants={sortedVariants}
            selectedVariant={selectedVariant}
            handleChange={handleChange}
          />
        )}
      </Container>
    </Wrapper>
  );
};

export { ProductDetailVariantSelection };
